import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Loader2, Users } from "lucide-react";
import ModalOrDrawer from "ui/ModalOrDrawer";
import { Button } from "ui/Button";
import EntityAvatar from "components/Entities/EntityAvatar";
import CoauthorSection from "components/Posts/sections/CoauthorSection";
import PrivacySection from "components/Posts/sections/PrivacySection";
import { useAuthGate } from "hooks/useAuthGate";
import { useCoauthorManager } from "hooks/useCoauthorManager";
import { collectionApi } from "lib/api";
import { addCoauthors, createCollection, type CollectionPostResponse } from "util/posts";
import { collectionQueryKeys } from "components/Collections/useCollections";
import { toast } from "sonner";
import { PrivacyLevel } from "util/userUtils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entityId: number;
  entityType: string;
  layerBase?: number;
  onCreated?: (collection: CollectionPostResponse) => void;
}

const NewCollectionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  layerBase,
  onCreated,
}) => {
  const queryClient = useQueryClient();
  const { currentUser, requireAuth } = useAuthGate();
  const [title, setTitle] = useState("");
  const [privacy, setPrivacy] = useState<PrivacyLevel>("me");
  const { coauthors, setCoauthors, addCoauthor, removeCoauthor, coauthorPairs } = useCoauthorManager(privacy);
  const [showCollaborators, setShowCollaborators] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setTitle("");
      setPrivacy("me");
      setCoauthors([]);
      setShowCollaborators(false);
      setIsSubmitting(false);
      setError("");
    }
  }, [isOpen, setCoauthors]);

  const canSubmit = useMemo(() => title.trim().length > 0 && !isSubmitting, [title, isSubmitting]);

  const handleCreate = async () => {
    if (!requireAuth() || !currentUser) {
      return;
    }
    const nextTitle = title.trim();
    if (!nextTitle) {
      setError("Collection name is required.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const created = await createCollection({
        issueId: null,
        title: nextTitle,
        body: null,
        url: null,
        audience: privacy,
        properties: { privacy },
      });

      if (coauthors.length > 0 && privacy !== "anonymous") {
        try {
          await addCoauthors("collection", created.id, coauthorPairs);
        } catch {
          /* best-effort */
        }
      }

      await collectionApi.addItem(created.id, { entityId, entityType });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: collectionQueryKeys.userCollections(entityType, entityId),
        }),
        queryClient.invalidateQueries({
          queryKey: collectionQueryKeys.saveState(entityType, entityId),
        }),
      ]);

      toast.success(`Saved to ${nextTitle}`);
      onCreated?.(created);
      onClose();
    } catch {
      setError("Failed to create collection.");
      toast.error("Failed to create collection");
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <div className="flex w-full items-center justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button type="button" onClick={handleCreate} disabled={!canSubmit}>
        {isSubmitting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : "Save"}
      </Button>
    </div>
  );

  return (
    <ModalOrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="New collection"
      footer={footer}
      stickyFooter={true}
      className="space-y-4"
      layerBase={layerBase}
    >
      <div className="space-y-4">
        <div className="rounded-2xl border border-eoe-icon-hover/60 bg-eoe-icon-hover/10 p-3 dark:border-eoe-dark-icon-hover/60 dark:bg-eoe-dark-icon-hover/20">
          <EntityAvatar
            entityId={entityId}
            entityType={entityType as
              | "issue"
              | "impact"
              | "solution"
              | "feedback"
              | "action"
              | "crosspost"
              | "comment"
              | "teabate"}
            isInputField={false}
            showLink={false}
          />
        </div>

        <div className="form-control">
          <label
            htmlFor="new-collection-name"
            className="mb-2 block text-sm font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading"
          >
            Collection name
          </label>
          <input
            id="new-collection-name"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Collection name"
            maxLength={300}
            className="w-full rounded-2xl border border-eoe-icon-hover bg-transparent px-4 py-3 text-base text-eoe-text-heading outline-none transition-colors placeholder:text-eoe-text-sub focus:border-eoe-icon dark:border-eoe-dark-icon-hover dark:text-eoe-dark-text-heading dark:placeholder:text-eoe-dark-text-sub dark:focus:border-eoe-dark-icon"
          />
        </div>

        <button
          type="button"
          onClick={() => setShowCollaborators((previous) => !previous)}
          className="flex w-full items-center gap-3 rounded-2xl px-2 py-3 text-left transition-colors hover:bg-eoe-icon-hover/40 dark:hover:bg-eoe-dark-icon-hover/50"
        >
          <div className="flex size-11 items-center justify-center rounded-full bg-eoe-icon-hover/60 dark:bg-eoe-dark-icon-hover/60">
            <Users className="size-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
              Add people to collection
            </div>
            <div className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              Save to a collection together
            </div>
          </div>
          <ChevronRight className={`size-5 transition-transform ${showCollaborators ? "rotate-90" : ""}`} aria-hidden />
        </button>

        {showCollaborators && privacy !== "anonymous" ? (
          <CoauthorSection
            coauthors={coauthors}
            onAdd={addCoauthor}
            onRemove={removeCoauthor}
            placeholder="Add people to collection"
          />
        ) : null}

        <PrivacySection
          privacy={privacy}
          onPrivacyChange={setPrivacy}
          miniIcon={false}
          showDropdown={true}
          label="Privacy"
        />

        {error ? (
          <div className="text-sm text-warning-red dark:text-warning-red-dark" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </ModalOrDrawer>
  );
};

export default NewCollectionModal;
